import { computed, Injectable, signal } from '@angular/core';
import { SessionService } from './session.service';

interface DataReporte {
  status: 'pendiente' | 'completado' | 'error';
  id: string; // id unico de la operacion
  rut?: string; // rut asociado
  url: string; // url para eventual redireccion
  idsReportes: Reporte[]; // datos de reportes
  idUsuario: string;
  organizacion: string;
  fechaFin?: number;
}

interface Reporte {
  reporte: string; // identificador de reporte
  idTransaccion: string;
  status?: 'pendiente' | 'completado' | 'error'; // detalle para lcv
  extraData?: any; // detalle para lcv
}

interface EventoFinCarga {
  id: string;
  timestamp: number;
  status: 'completado' | 'error';
}

@Injectable({
  providedIn: 'root'
})
export class ReporteStateService {
  private estadoMap = signal<Record<string, DataReporte>>({});
  
  readonly listaSolicitudes = computed(() => Object.values(this.estadoMap()));

  private notificacionFin = signal<EventoFinCarga | null>(null);

  readonly eventoFinProceso = this.notificacionFin.asReadonly();
  
  constructor(
    private _sessionService: SessionService
  ) { }

  iniciarSolicitud(idUnico: string, rut: string, urlBase: string, idsReportes: Reporte[]) {
    const nuevaSolicitud: DataReporte = {
      id: idUnico,
      rut: rut,
      url: urlBase,
      status: 'pendiente',
      idsReportes: idsReportes,
      idUsuario: this._sessionService.getUserInfo().username,
      organizacion: this._sessionService.getOrganization()
    };

    this.estadoMap.update(mapaActual => ({
      ...mapaActual,
      [idUnico]: nuevaSolicitud
    }));
  }

  // En el servicio
  actualizarSolicitud(
    idUnico: string, 
    idsReportes: Reporte[] = [], 
    estado?: 'pendiente' | 'completado' | 'error', 
    rut?: string, 
    urlBase?: string 
  ) {
    
    if(!idUnico) { console.error("Id no valido"); return; }
    
    if(estado && !['pendiente', 'completado', 'error'].includes(estado)){
      console.error("Estado invalido"); return; 
    }

    this.estadoMap.update(mapaActual => {
      const existe = mapaActual[idUnico];

      if (!existe) {
        if(!estado) { console.error("Estado requerido"); return mapaActual; }
        if (!rut || !urlBase) { console.error("Faltan datos"); return mapaActual; }

        if(estado === 'error'){ 
          this.notificacionFin.set({
            id: idUnico,
            timestamp: Date.now(),
            status: 'error'
          });
          return mapaActual
        }

        const nuevaSolicitud: DataReporte = {
          id: idUnico,
          rut: rut,
          url: urlBase,
          status: estado,
          idsReportes: idsReportes,
          idUsuario: this._sessionService.getUserInfo().username,
          organizacion: this._sessionService.getOrganization()
        };
        return { ...mapaActual, [idUnico]: nuevaSolicitud };
      } 
      
      if(!estado) { 
        idsReportes = this.addReporteFinanciero(existe.idsReportes, idsReportes);
      }

      let nuevaFechaFin = existe.fechaFin;
      if (estado === 'completado' || estado === 'error') {
        nuevaFechaFin = Date.now();
      }
      
      const solicitudActualizada: DataReporte = {
        ...existe,
        status: estado ? estado : existe.status,
        idsReportes: idsReportes.length > 0 ? this.updateIdsReportes(existe.idsReportes, idsReportes) : existe.idsReportes,
        rut: rut || existe.rut,
        url: urlBase || existe.url,
        fechaFin: nuevaFechaFin
      };

      return { ...mapaActual, [idUnico]: solicitudActualizada };
    });


    if (estado === 'completado' || estado === 'error') {
      this.notificacionFin.set({
        id: idUnico,
        timestamp: Date.now(),
        status: estado
      });
    }
  }

  updateIdsReportes(existentes: Reporte[], nuevos: Reporte[]): Reporte[] {
    if (!nuevos || nuevos.length === 0) {
      return [];
    }

    if (!existentes || existentes.length === 0) {
      return [...nuevos];
    }

    return nuevos.map(nuevoItem => {

      const itemPrevio = existentes.find(e => {
        if (e.reporte === 'lcv' && nuevoItem.reporte === 'lcv') {
          return e.extraData?.periodo === nuevoItem.extraData?.periodo;
        }
        return e.reporte === nuevoItem.reporte;
      });

      if (itemPrevio) {
        return {
          ...itemPrevio,
          ...nuevoItem
        };
      }

      return { ...nuevoItem };
    });
  }

  addReporteFinanciero(existentes: Reporte[], nuevos: Reporte[]): Reporte[] {
    if (!existentes || existentes.length === 0) {
      return [...(nuevos || [])];
    }

    const resultado = [...(nuevos || [])];
    const reporteFinanciero = existentes.find(e => e.reporte === 'reporteFinanciero');
    
    if(reporteFinanciero && !resultado.find(e => e.reporte === 'reporteFinanciero')) {
      resultado.push({ ...reporteFinanciero });
    }

    return resultado;
  }

  obtenerReporte(id: string): DataReporte | undefined {
    return this.estadoMap()[id];
  }

  obtenerSolicitudesPorModulo(urlModulo: string): DataReporte[] {
    const usuarioActual = this._sessionService.getUserInfo().username;
    const orgActual = this._sessionService.getOrganization();

    return Object.values(this.estadoMap()).filter(req => 
      req.idUsuario === usuarioActual &&
      req.organizacion === orgActual &&
      req.url.startsWith(urlModulo)
    );
  }

  eliminarSolicitud(idUnico: string): void {
    this.estadoMap.update(mapa => {
      const { [idUnico]: eliminado, ...resto } = mapa;
      return resto;
    });
  }

}
